/**
 * Shared schema.org structured-data objects, rendered as JSON-LD by both
 * routers: `pages/_app.tsx` injects them today (via next-seo /
 * `next/script`), and the App Router root layout server-renders the same
 * objects. Keeping the plain objects here means the two routers can't drift.
 */

import { HOME_SEO } from './productCategoryMetadata'

/** WebSite entity with a SearchAction (Google sitelinks search box). */
export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://dupe.com/#website',
  name: 'Dupe.com',
  url: 'https://dupe.com',
  publisher: { '@id': 'https://dupe.com/#organization' },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://dupe.com/search?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
}

/**
 * Organization entity — rendered verbatim by BOTH routers now: the App Router
 * layout and `pages/_app.tsx` each serialize this object into a
 * `application/ld+json` script. `_app.tsx` used to re-declare the same fields
 * as props on next-seo's `<OrganizationJsonLd />`, which meant every addition
 * had to be made twice and could silently drift; rendering the shared object
 * in both places makes that impossible.
 *
 * `legalName`, `address` and `contactPoint` let assistants verify the business
 * and answer contact questions without scraping. They must stay consistent
 * with the entity and address published in the privacy policy
 * (`public/legal/dupe-privacy_26-02-19.html`) — that document is the source of
 * truth for the legal entity, and a mismatch reads as a legitimacy signal
 * failure rather than a typo.
 */
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://dupe.com/#organization',
  name: 'Dupe.com',
  legalName: 'Carrot Cart Inc.',
  url: 'https://dupe.com',
  logo: 'https://dupe.com/android-chrome-512x512.png',
  description:
    'Dupe.com is an AI visual search engine that finds visually similar products ("dupes") from thousands of retailers at lower prices, across furniture, home decor, fashion, and beauty.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1270 Ave of the Americas, 7th Fl 1013',
    addressLocality: 'New York',
    addressRegion: 'NY',
    postalCode: '10020',
    addressCountry: 'US',
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@dupe.com',
      url: 'https://dupe.com/contact',
      availableLanguage: ['en'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'legal',
      email: 'legal@dupe.com',
      availableLanguage: ['en'],
    },
  ],
  // Entity-disambiguation links. Beyond the social profiles, list every
  // official presence that exists at a stable public URL — this is how AI
  // assistants and knowledge graphs tell Dupe.com apart from similarly named
  // things. Only link profiles that really exist and that we control; add
  // Wikipedia/Wikidata here if/when those entities are created (they don't
  // exist yet as of 2026-08 — see docs/references/agent-readiness-orank.md).
  sameAs: [
    'https://www.instagram.com/dupe_com',
    'https://x.com/dupedotcom',
    'https://www.tiktok.com/@dupedotcom',
    'https://github.com/dupe-com',
    'https://apps.apple.com/us/app/dupe-com-up-to-90-savings/id1605692440',
    'https://chromewebstore.google.com/detail/dupecom-find-similar-prod/pkhpcfoaocmmpmphhenoblggeinedlah',
  ],
}

/**
 * Homepage WebPage entity, rendered only by `pages/index.tsx`.
 *
 * No `speakable` specification here: it used to point at a server-rendered
 * "What is Dupe.com?" section (`HomeAboutDupe.tsx`), which was pulled after
 * shipping unintentionally at the bottom of the live homepage (regression
 * from PR #4718). Re-add `speakable` only alongside a real, deliberately
 * placed no-JS section — never re-point it at ids nothing renders.
 */
export const homePageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://dupe.com/#webpage',
  url: 'https://dupe.com',
  name: HOME_SEO.title,
  description: HOME_SEO.description,
  isPartOf: { '@id': 'https://dupe.com/#website' },
  about: { '@id': 'https://dupe.com/#organization' },
}
